function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import clsx from 'clsx';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { SearchDisplayContext } from 'common/report/util/search/types';
import { InputReact } from 'common/components/input';
import { QueryReportHeaderSave } from 'common/query-builder/query-report-header/types';
import { emitTrackEvent, KEY_VALUES } from 'common/util/dom';
import { truncate } from 'common/util/string';
import { MAX_REPORT_DESCRIPTION_LENGTH } from 'common/constants';
import { useTranslation } from 'common/i18n/hooks';
import entityNameInputStyleOverride from './entity-name-input.inline.styl';
import styles from './index.module.styl';
import { QueryReportTitleProps, TrackingEvent } from './types';
export const QueryReportTitleReact: React.FC<QueryReportTitleProps> = props => {
  const [isEditingEntityName, setIsEditingEntityName] = useState<boolean>(false);
  const [entityName, setEntityName] = useState<string>(null);
  const [isEditingEntityDescription, setIsEditingEntityDescription] = useState<boolean>(false);
  const [entityDescription, setEntityDescription] = useState<string>(null);
  const canEdit = canEditExistingEntity();
  const rootEl = useRef<HTMLDivElement>(null);
  const {
    t
  } = useTranslation();
  if (props.entityDescription !== entityDescription) {
    setEntityDescription(props.entityDescription);
  }
  if (props.entityName !== entityName) {
    setEntityName(props.entityName);
  }
  function dispatchTrack(eventName: string): void {
    let trackingProps: Record<string, any> = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    emitTrackEvent(rootEl.current, {
      eventName,
      eventPrefix: "mp-query-report-title",
      props: _objectSpread({
        project_id: props.projectId,
        tab: props.trackName,
        workspace_id: props.workspaceId
      }, trackingProps)
    });
  }
  function hasSelectedEntity(): boolean {
    return Boolean(props.selectedEntityId);
  }
  function allowSuperAdminOverride(): boolean {
    /** Allow super admin override for an entity that is valid but not save-able (due to permission) and user is a superadmin */
    return !props.canSave && !props.invalidEntity && props.isSuperadmin || props.allowStaffOverride;
  }
  function canEditExistingEntity(): boolean {
    return hasSelectedEntity() && (props.canWrite && props.canSave || allowSuperAdminOverride());
  }
  function isDashboardModeEnabled(): boolean {
    return props.displayContext === SearchDisplayContext.Dashboard;
  }
  function canSave(): boolean {
    return !props.invalidEntity && (props.canSave || allowSuperAdminOverride());
  }
  function hasUnsavedChanges(): boolean {
    // if there is no entity selected/saved, then by definition there are unsaved changes
    return !hasSelectedEntity() || props.hasUnsavedChanges;
  }
  function getEntityName(): string {
    if (isDashboardModeEnabled()) {
      // length of the empty space for the name
      let truncatedLength = 120;
      // make space for the view only copy on the right
      if (!canSave() && hasUnsavedChanges()) {
        truncatedLength = 99;
      }
      return truncate(entityName, truncatedLength) || props.emptyEntityName;
    }
    return entityName || props.emptyEntityName;
  }
  function dispatchSave(update: {
    entityName?: string;
    entityDescription?: string;
  }): void {
    var _update$entityName, _update$entityDescrip;
    const saveData: QueryReportHeaderSave = {
      name: (_update$entityName = update.entityName) !== null && _update$entityName !== void 0 ? _update$entityName : entityName,
      description: (_update$entityDescrip = update.entityDescription) !== null && _update$entityDescrip !== void 0 ? _update$entityDescrip : entityDescription,
      didSave: true,
      saveAsNew: false
    };
    props.onSave(saveData);
    dispatchTrack(TrackingEvent.ReportSaved);
  }
  function onChangeEntityName(name: string): void {
    setIsEditingEntityName(false);
    setEntityName(name);
    dispatchSave({
      entityName: name
    });
  }
  function onChangeEntityDescription(description: string): void {
    setIsEditingEntityDescription(false);
    setEntityDescription(description);
    dispatchSave({
      entityDescription: description
    });
  }
  function onEditEntityName(): void {
    setIsEditingEntityName(true);
    dispatchTrack(TrackingEvent.StartReportRename);
  }
  function onEditEntityDescription(): void {
    setIsEditingEntityDescription(true);
    dispatchTrack(TrackingEvent.StartReportEditDescription);
  }
  const AddEntityNameButton: React.FC = () => {
    return <div className={clsx({
      [styles.canEdit]: canEdit,
      [styles.entityName]: true
    })} onClick={() => {
      if (canEdit) {
        onEditEntityName();
      }
    }} data-sentry-component="AddEntityNameButton" data-sentry-source-file="index.tsx">
        {getEntityName()}
      </div>;
  };
  const EditEntityNameInput: React.FC = () => {
    const inputRef = useRef(null);
    useLayoutEffect(() => {
      inputRef.current.focus();
    }, []);
    return <InputReact ref={inputRef} className={styles.entityNameInput}
    // @ts-ignore
    styleOverride={entityNameInputStyleOverride} value={getEntityName()} onBlur={(ev: FocusEvent): void => {
      onChangeEntityName((ev.target as HTMLInputElement).value);
    }} onKeydown={(ev: KeyboardEvent): void => {
      if (ev.key === KEY_VALUES.ESCAPE) {
        ev.preventDefault();
        setIsEditingEntityName(false);
      } else if (ev.key === KEY_VALUES.ENTER) {
        ev.preventDefault();
        onChangeEntityName((ev.target as HTMLInputElement).value);
      }
    }} data-sentry-element="InputReact" data-sentry-component="EditEntityNameInput" data-sentry-source-file="index.tsx" />;
  };
  const AddEntityDescriptionButton: React.FC = () => {
    return <div className={clsx(styles.entityDescription, {
      [styles.canEdit]: canEdit
    })} title={entityDescription} onClick={onEditEntityDescription} data-sentry-component="AddEntityDescriptionButton" data-sentry-source-file="index.tsx">
        {entityDescription || "+ ".concat(t("boards.header.description-placeholder"))}
      </div>;
  };
  const EditEntityDescriptionInput: React.FC = () => {
    const inputRef = useRef(null);
    useLayoutEffect(() => {
      inputRef.current.focus();
    }, []);
    function onInputChanged(value: string) {
      const truncatedDescription = value.substring(0, MAX_REPORT_DESCRIPTION_LENGTH);
      onChangeEntityDescription(truncatedDescription);
    }
    return <InputReact ref={inputRef} selectOnFocus value={props.entityDescription} onBlur={(ev: FocusEvent): void => {
      onInputChanged((ev.target as HTMLInputElement).value);
    }} onKeydown={(ev: KeyboardEvent): void => {
      if (ev.key === KEY_VALUES.ESCAPE) {
        ev.preventDefault();
        setIsEditingEntityDescription(false);
      } else if (ev.key === KEY_VALUES.ENTER) {
        ev.preventDefault();
        onInputChanged((ev.target as HTMLInputElement).value);
      }
    }} data-sentry-element="InputReact" data-sentry-component="EditEntityDescriptionInput" data-sentry-source-file="index.tsx" />;
  };
  return <div ref={rootEl} className={clsx(props.className, {
    [styles.dashboardMode]: isDashboardModeEnabled(),
    [styles.queryReportTitle]: true
  })} data-sentry-component="QueryReportTitleReact" data-sentry-source-file="index.tsx">
      {isEditingEntityName ? <EditEntityNameInput /> : <AddEntityNameButton />}
      <div>
        {isEditingEntityDescription && <div className={styles.fullWidth}>
            <EditEntityDescriptionInput />
          </div>}
        {canEditExistingEntity() && !isEditingEntityDescription && <AddEntityDescriptionButton />}
      </div>
    </div>;
};